import type { ReferenceData } from '@domain';
import type { MoneyEntry, PaymentOptions, TrainingDelivery } from '../api.js';
import type { Lang, Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';
import { PaymentScreen } from './PaymentScreen.js';
import { SpendReceiveForm } from './SpendReceiveForm.js';
import { TransferForm } from './TransferForm.js';

/**
 * Every way money moves, in one place.
 *
 * The screen used to record one of them: a payment against an invoice. That
 * covers the money a client's books already expect and none of the rest — the
 * bank charge, the fuel receipt, the interest a deposit earned, the cash drawn
 * into petty cash every Thursday. Those are most of a small company's bank
 * statement, and until now every one of them was an email to us.
 *
 * Xero puts the same four behind one key each: Spend money, Receive money,
 * Transfer money, and a payment against an invoice. They are four tabs rather
 * than four menu entries because somebody who has money to record should not
 * have to know which Xero object it becomes before they can find the page.
 *
 * Which object each becomes is in api/src/domain/money.ts, and the difference
 * matters: a transfer written as two bank transactions is two halves that can
 * drift apart.
 */

export type MoneyTab = 'invoice' | 'spend' | 'receive' | 'transfer';

const TABS: { key: MoneyTab; label: string }[] = [
  { key: 'invoice', label: 'Against an invoice' },
  { key: 'spend', label: 'Spend money' },
  { key: 'receive', label: 'Receive money' },
  { key: 'transfer', label: 'Transfer money' },
];

const SUBTITLES: Record<MoneyTab, string> = {
  invoice: 'Against an approved invoice. A draft cannot be paid — it is not a debt yet.',
  spend: 'Money leaving a bank account with no bill behind it — a charge, a receipt, a direct purchase.',
  receive: 'Money arriving with no invoice behind it — a cash sale, interest, a refund.',
  transfer: 'Money moving between two of your own accounts. Nothing is spent and nothing is earned.',
};

export function MoneyScreen({
  tab,
  onTab,
  options,
  reference,
  deliveries,
  currency,
  loading,
  busy,
  error,
  done,
  lang,
  t,
  justFiled,
  onLeaveUnpaid,
  onRecordPayment,
  onRecordMoney,
}: {
  tab: MoneyTab;
  onTab: (tab: MoneyTab) => void;
  options: PaymentOptions | null;
  reference: ReferenceData;
  /** Open training courses money out can be put on. Empty for most. */
  deliveries: TrainingDelivery[];
  currency: string;
  loading: boolean;
  busy: boolean;
  error: string | null;
  done: string | null;
  lang: Lang;
  /** A document filed moments ago, arrived at straight from the form. */
  justFiled?: { id: string; number: string } | null;
  /** Leave that document owing and go back. */
  onLeaveUnpaid?: () => void;
  t: Translate;
  onRecordPayment: (payment: {
    invoiceId: string;
    accountCode: string;
    method: string;
    date: string;
    amount: string;
    reference: string;
  }) => void;
  onRecordMoney: (entry: MoneyEntry) => void;
}) {
  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '920px' }}>
      <PageTitle title={t('Money in and out')} sub={t(SUBTITLES[tab])} />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          margin: '16px 0',
        }}
      >
        {TABS.map((entry) => {
          const picked = entry.key === tab;
          return (
            <button
              key={entry.key}
              type="button"
              onClick={() => onTab(entry.key)}
              style={{
                background: picked ? theme.nav : 'transparent',
                color: picked ? '#fff' : theme.text,
                border: `1px solid ${picked ? theme.nav : theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                padding: '9px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t(entry.label)}
            </button>
          );
        })}
      </div>

      {/*
        One place for the answer, whichever tab asked the question. Four copies
        of this would be four chances for one of them to stay on screen after
        the next entry, telling somebody their transfer worked when what worked
        was the payment before it.
      */}
      {done && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{ padding: '13px 16px', color: theme.goodDeep, fontWeight: 600, fontSize: '13px' }}
          >
            {t(done)}
          </div>
        </Card>
      )}

      {error && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{ padding: '13px 16px', color: theme.badDeep, fontWeight: 600, fontSize: '13px' }}
          >
            {t(error)}
          </div>
        </Card>
      )}

      {tab === 'invoice' && (
        <PaymentScreen
          options={options}
          loading={loading}
          busy={busy}
          lang={lang}
          justFiled={justFiled}
          onLeaveUnpaid={onLeaveUnpaid}
          t={t}
          onRecord={onRecordPayment}
        />
      )}

      {(tab === 'spend' || tab === 'receive') && (
        /*
          Keyed on the tab so switching between spending and receiving starts
          a clean form. They look alike and mean opposite things; carrying the
          lines across is how money out gets filed as money in.
        */
        <SpendReceiveForm
          key={tab}
          direction={tab}
          reference={reference}
          deliveries={deliveries}
          currency={currency}
          busy={busy}
          lang={lang}
          t={t}
          onRecord={onRecordMoney}
        />
      )}

      {tab === 'transfer' && (
        <TransferForm
          reference={reference}
          currency={currency}
          busy={busy}
          lang={lang}
          t={t}
          onRecord={onRecordMoney}
        />
      )}
    </div>
  );
}
